import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Power, Phone, MapPin, Navigation, Truck, CheckCircle2,
  Clock, History, LogOut, Loader2, MessageCircle, DollarSign,
} from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { aplicarFaviconPagina, aplicarTituloPagina } from "../../lib/branding";

interface Turno {
  user_id: string;
  en_turno: boolean;
  telefono?: string;
  zona?: string;
  comision_por_pedido_usd: string;
  ultima_actividad?: string;
}

interface PedidoRepartidor {
  id: string;
  numeroPedido: string;
  nombreCliente: string;
  telefono?: string;
  email?: string;
  direccionEntrega?: string;
  referenciaDireccion?: string;
  lat?: string;
  lng?: string;
  notasCliente?: string;
  subtotal: string;
  costoEnvio: string;
  total: string;
  estado: string;
  estadoPago: string;
  horaSolicitud: string;
  horaAsignado?: string;
  horaEnCamino?: string;
  comisionUsd?: string;
  numItems: number;
}

function urlMaps(p: PedidoRepartidor): string {
  if (p.lat && p.lng) {
    return `https://www.google.com/maps/dir/?api=1&destination=${p.lat},${p.lng}`;
  }
  const dir = encodeURIComponent(p.direccionEntrega ?? "");
  return `https://www.google.com/maps/search/?api=1&query=${dir}`;
}

function urlWhatsApp(telefono: string): string {
  const limpio = telefono.replace(/[^0-9]/g, "");
  return `https://wa.me/${limpio}`;
}

function fmtUsd(v: string | number | undefined): string {
  const n = Number(v ?? 0);
  return "$" + n.toFixed(2);
}

function tiempoTranscurrido(iso?: string): string {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  return `${h}h ${min % 60}m`;
}

const ESTADO_LABEL: Record<string, { txt: string; color: string }> = {
  confirmado:     { txt: "Confirmado",     color: "bg-blue-100 text-blue-700 border-blue-200" },
  en_preparacion: { txt: "En preparación", color: "bg-amber-100 text-amber-700 border-amber-200" },
  listo:          { txt: "Listo para retirar", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  en_camino:      { txt: "En camino",      color: "bg-purple-100 text-purple-700 border-purple-200" },
};

export default function RepartidorPage() {
  const { logout, user } = useAuth();
  const qc = useQueryClient();
  const [vista, setVista] = useState<"activos" | "historial">("activos");
  const [sucursalNombre, setSucursalNombre] = useState<string | null>(null);

  // Favicon/título de la pestaña y nombre visible: la sucursal a la que este
  // repartidor está asignado (si es solo una) -- si no está asignado a
  // ninguna (o a varias), usa el branding del grupo (misma logica que
  // TiendaLayout para el resto de roles).
  useEffect(() => {
    const sucursalIdBranding = user?.sucursalIds?.length === 1 ? user.sucursalIds[0] : 1;
    fetch(`/api/v1/menu-publico/sucursal/${sucursalIdBranding}/config`)
      .then(r => r.json())
      .then(d => {
        aplicarFaviconPagina(d.logoUrl);
        aplicarTituloPagina(d.sucursalNombre ?? d.descripcionTienda?.split(".")[0]?.trim());
        setSucursalNombre(d.sucursalNombre ?? null);
      })
      .catch(() => {});
  }, [user?.sucursalIds]);

  const turnoQ = useQuery({
    queryKey: ["repartidor", "turno"],
    queryFn: () => api<Turno>("/v1/repartidor/turno"),
  });

  const pedidosQ = useQuery({
    queryKey: ["repartidor", "mis-pedidos"],
    queryFn: () => api<PedidoRepartidor[]>("/v1/repartidor/mis-pedidos"),
    refetchInterval: 15_000, // refrescar cada 15s
    enabled: vista === "activos",
  });

  const historialQ = useQuery({
    queryKey: ["repartidor", "historial"],
    queryFn: () => api<{
      resumen: { entregados: number; totalComisionUsd: string; ventasEntregadasUsd: string; minutosPromedio: string; };
      pedidos: any[];
    }>("/v1/repartidor/historial"),
    enabled: vista === "historial",
  });

  const setTurno = useMutation({
    mutationFn: (enTurno: boolean) =>
      api("/v1/repartidor/turno", { method: "PATCH", body: { enTurno } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["repartidor", "turno"] }),
  });

  const enCamino = useMutation({
    mutationFn: (id: string) => api(`/v1/repartidor/pedidos/${id}/en-camino`, { method: "PATCH" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["repartidor", "mis-pedidos"] }),
  });

  const entregado = useMutation({
    mutationFn: (id: string) => api(`/v1/repartidor/pedidos/${id}/entregado`, { method: "PATCH", body: {} }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["repartidor", "mis-pedidos"] });
      qc.invalidateQueries({ queryKey: ["repartidor", "historial"] });
    },
  });

  const turno = turnoQ.data;
  const enTurno = !!turno?.en_turno;
  const pedidos = pedidosQ.data ?? [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-ink-50 to-paper-50 pb-20">
      {/* HEADER STICKY */}
      <header className="sticky top-0 z-30 bg-white border-b border-ink-200 shadow-sm">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <div
            className={`size-3 rounded-full ${enTurno ? "bg-emerald-500 animate-pulse" : "bg-ink-300"}`}
            title={enTurno ? "En turno" : "Fuera de turno"}
          />
          <div className="flex-1 min-w-0">
            <p className="font-bold text-ink-900 text-sm leading-none">
              {user?.fullName ?? "Repartidor"}
            </p>
            <p className="text-[11px] text-ink-500 mt-0.5">
              {sucursalNombre && <>{sucursalNombre} · </>}
              {enTurno ? "🟢 En turno" : "⚪ Fuera de turno"}
              {turno?.comision_por_pedido_usd && (
                <> · {fmtUsd(turno.comision_por_pedido_usd)} por pedido</>
              )}
            </p>
          </div>
          <button
            onClick={() => setTurno.mutate(!enTurno)}
            disabled={setTurno.isPending}
            className={`px-4 py-2 rounded-full font-bold text-sm transition flex items-center gap-1.5 shadow ${
              enTurno
                ? "bg-rose-100 text-rose-700 border border-rose-200 hover:bg-rose-200"
                : "bg-emerald-500 text-white hover:bg-emerald-600"
            }`}
          >
            <Power className="size-4" />
            {enTurno ? "Cerrar turno" : "Iniciar turno"}
          </button>
        </div>

        {/* TABS */}
        <div className="max-w-2xl mx-auto px-4 grid grid-cols-2 border-t border-ink-100">
          <button onClick={() => setVista("activos")}
            className={`py-2.5 text-sm font-medium flex items-center justify-center gap-1.5 border-b-2 ${
              vista === "activos"
                ? "border-brand-500 text-brand-700"
                : "border-transparent text-ink-500"
            }`}>
            <Truck className="size-4" />
            Activos {pedidos.length > 0 && <span className="ml-1 bg-brand-600 text-white text-xs px-1.5 rounded-full">{pedidos.length}</span>}
          </button>
          <button onClick={() => setVista("historial")}
            className={`py-2.5 text-sm font-medium flex items-center justify-center gap-1.5 border-b-2 ${
              vista === "historial"
                ? "border-brand-500 text-brand-700"
                : "border-transparent text-ink-500"
            }`}>
            <History className="size-4" />
            Historial
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-3 sm:px-4 pt-4">
        {!enTurno && vista === "activos" && (
          <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4 text-center mb-4">
            <p className="text-amber-800 font-medium">
              📴 Estás fuera de turno
            </p>
            <p className="text-xs text-amber-700 mt-1">
              No se te asignarán pedidos nuevos. Tocá <b>"Iniciar turno"</b> arriba cuando estés listo.
            </p>
          </div>
        )}

        {/* ACTIVOS */}
        {vista === "activos" && (
          <>
            {pedidosQ.isLoading ? (
              <Cargando />
            ) : pedidos.length === 0 ? (
              <Vacio enTurno={enTurno} />
            ) : (
              <div className="space-y-3">
                {pedidos.map((p) => (
                  <PedidoCard key={p.id} pedido={p}
                    onEnCamino={() => enCamino.mutate(p.id)}
                    onEntregado={() => entregado.mutate(p.id)}
                    cargandoEnCamino={enCamino.isPending}
                    cargandoEntrega={entregado.isPending} />
                ))}
              </div>
            )}
          </>
        )}

        {/* HISTORIAL */}
        {vista === "historial" && historialQ.data && (
          <div>
            <ResumenHistorial r={historialQ.data.resumen} />
            <div className="space-y-2 mt-4">
              {historialQ.data.pedidos.map((p: any) => (
                <div key={p.id} className="bg-white rounded-xl p-3 border border-ink-200 flex items-center gap-3">
                  <CheckCircle2 className="size-5 text-emerald-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-ink-800 truncate">
                      #{p.numeroPedido} · {p.nombreCliente}
                    </p>
                    <p className="text-[11px] text-ink-500">
                      {new Date(p.horaRealEntrega).toLocaleString("es-VE", {
                        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
                      })}
                      {p.minutosEntrega && ` · ${Math.round(p.minutosEntrega)} min`}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-emerald-700 text-sm">+{fmtUsd(p.comisionUsd)}</p>
                    <p className="text-[10px] text-ink-400">{fmtUsd(p.total)} total</p>
                  </div>
                </div>
              ))}
              {historialQ.data.pedidos.length === 0 && (
                <p className="text-center text-ink-400 py-8 text-sm">Aún no tenés entregas registradas.</p>
              )}
            </div>
          </div>
        )}
      </main>

      {/* FOOTER FIJO con logout */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-ink-200 px-4 py-2">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <Link to="/perfil" className="text-xs text-ink-500 hover:text-ink-700">
            Mi perfil
          </Link>
          <button onClick={() => logout()}
            className="text-xs text-ink-500 hover:text-rose-600 flex items-center gap-1">
            <LogOut className="size-3" /> Salir
          </button>
        </div>
      </div>
    </div>
  );
}

function Cargando() {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-ink-400">
      <Loader2 className="size-8 animate-spin mb-2" />
      <p className="text-sm">Buscando pedidos…</p>
    </div>
  );
}

function Vacio({ enTurno }: { enTurno: boolean }) {
  return (
    <div className="text-center py-16">
      <p className="text-6xl mb-3">🛵</p>
      <p className="font-medium text-ink-700 mb-1">
        {enTurno ? "No tenés pedidos asignados" : "Estás fuera de turno"}
      </p>
      <p className="text-xs text-ink-500">
        {enTurno
          ? "Cuando el admin te asigne un pedido lo vas a ver acá."
          : "Cuando inicies turno y te asignen pedidos los verás acá."}
      </p>
    </div>
  );
}

function ResumenHistorial({ r }: { r: any }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      <Stat label="Entregados" value={r.entregados} icono="✅" />
      <Stat label="Honorarios" value={fmtUsd(r.totalComisionUsd)} icono="💵" highlight />
      <Stat label="Min/entrega" value={`${Math.round(Number(r.minutosPromedio ?? 0))}'`} icono="⏱️" />
    </div>
  );
}

function Stat({ label, value, icono, highlight }: { label: string; value: any; icono: string; highlight?: boolean }) {
  return (
    <div className={`rounded-xl p-3 text-center border ${highlight ? "bg-emerald-50 border-emerald-200" : "bg-white border-ink-200"}`}>
      <div className="text-xl">{icono}</div>
      <div className={`font-bold text-lg ${highlight ? "text-emerald-700" : "text-ink-800"}`}>{value}</div>
      <div className="text-[10px] text-ink-500 uppercase tracking-wide">{label}</div>
    </div>
  );
}

function PedidoCard({
  pedido, onEnCamino, onEntregado, cargandoEnCamino, cargandoEntrega,
}: {
  pedido: PedidoRepartidor;
  onEnCamino: () => void;
  onEntregado: () => void;
  cargandoEnCamino: boolean;
  cargandoEntrega: boolean;
}) {
  const estado = ESTADO_LABEL[pedido.estado] ?? { txt: pedido.estado, color: "bg-ink-100 text-ink-700" };
  const esEnCamino = pedido.estado === "en_camino";
  const puedeMarcarEnCamino = ["confirmado", "listo", "en_preparacion"].includes(pedido.estado);
  return (
    <div className={`bg-white rounded-2xl border-2 shadow-sm overflow-hidden ${esEnCamino ? "border-purple-300" : "border-ink-200"}`}>
      {/* HEADER */}
      <div className="p-3 border-b border-ink-100 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-bold text-ink-900 text-base leading-tight">#{pedido.numeroPedido}</p>
          <p className="text-xs text-ink-500 mt-0.5">
            <Clock className="inline size-3 -mt-0.5 mr-0.5" />
            hace {tiempoTranscurrido(pedido.horaSolicitud)}
            · {pedido.numItems} {pedido.numItems === 1 ? "item" : "items"}
          </p>
        </div>
        <span className={`text-xs px-2 py-1 rounded-full font-semibold border whitespace-nowrap ${estado.color}`}>
          {estado.txt}
        </span>
      </div>

      {/* CLIENTE */}
      <div className="p-3 space-y-2">
        <div>
          <p className="text-[10px] uppercase tracking-wide text-ink-400 font-semibold">Cliente</p>
          <p className="font-medium text-ink-800 text-sm">{pedido.nombreCliente}</p>
        </div>

        {pedido.direccionEntrega && (
          <div>
            <p className="text-[10px] uppercase tracking-wide text-ink-400 font-semibold">Dirección</p>
            <p className="text-sm text-ink-700">{pedido.direccionEntrega}</p>
            {pedido.referenciaDireccion && (
              <p className="text-xs text-ink-500 italic">📍 {pedido.referenciaDireccion}</p>
            )}
          </div>
        )}

        {pedido.notasCliente && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
            <p className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold">⚠️ Notas del cliente</p>
            <p className="text-xs text-amber-900 mt-0.5">{pedido.notasCliente}</p>
          </div>
        )}

        {/* TOTALES */}
        <div className="flex items-end justify-between pt-1 border-t border-ink-100">
          <div className="text-xs text-ink-500">
            Subtotal: {fmtUsd(pedido.subtotal)}{" "}
            {Number(pedido.costoEnvio) > 0 && (<>· Envío: {fmtUsd(pedido.costoEnvio)}</>)}
          </div>
          <div className="text-right">
            <p className="font-bold text-ink-900 text-lg leading-none">{fmtUsd(pedido.total)}</p>
            {pedido.comisionUsd && (
              <p className="text-[10px] text-emerald-700 mt-0.5">
                <DollarSign className="inline size-3 -mt-0.5" />
                +{fmtUsd(pedido.comisionUsd)} para ti
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ACCIONES */}
      <div className="grid grid-cols-2 gap-0 border-t border-ink-200">
        {pedido.telefono && (
          <>
            <a href={`tel:${pedido.telefono}`}
              className="flex items-center justify-center gap-1.5 py-3 text-sm font-medium text-blue-700 hover:bg-blue-50 border-r border-ink-200">
              <Phone className="size-4" /> Llamar
            </a>
            <a href={urlWhatsApp(pedido.telefono)} target="_blank" rel="noopener"
              className="flex items-center justify-center gap-1.5 py-3 text-sm font-medium text-emerald-700 hover:bg-emerald-50">
              <MessageCircle className="size-4" /> WhatsApp
            </a>
          </>
        )}
      </div>

      {pedido.direccionEntrega && (
        <a href={urlMaps(pedido)} target="_blank" rel="noopener"
          className="flex items-center justify-center gap-2 py-3 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 transition border-t border-ink-200">
          <Navigation className="size-4" />
          Cómo llegar (Google Maps)
        </a>
      )}

      {/* CTA principal */}
      <div className="p-3 bg-ink-50 border-t border-ink-200">
        {puedeMarcarEnCamino && (
          <button onClick={onEnCamino} disabled={cargandoEnCamino}
            className="w-full py-3 bg-purple-600 text-white font-bold rounded-xl hover:bg-purple-700 transition disabled:opacity-50 flex items-center justify-center gap-2">
            {cargandoEnCamino ? <Loader2 className="size-5 animate-spin" /> : <Truck className="size-5" />}
            Salí con el pedido
          </button>
        )}
        {esEnCamino && (
          <button onClick={onEntregado} disabled={cargandoEntrega}
            className="w-full py-3 bg-emerald-600 text-white font-bold rounded-xl hover:bg-emerald-700 transition disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg">
            {cargandoEntrega ? <Loader2 className="size-5 animate-spin" /> : <CheckCircle2 className="size-5" />}
            Marcar como entregado
          </button>
        )}
      </div>
    </div>
  );
}
